/**
 * Revenue Stats two-row month grid (WSRADM-0034 / WSRADM-0036).
 *
 * Row A = Avg per member (ARM + family averages); Row B = Total revenue.
 * Counts and money are rendered as sent — never summed or divided.
 * Non-zero Overlap opens OverlapPopover (products as sent; never reconciled).
 * Hidden columns (WSRADM-0051) are omitted only — no visible total replaces them.
 */
import { Fragment, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatMoney, formatMonthLabel } from '@/api/reports/format';
import { appendReportParams, familySlugToPath } from '@/api/reports/reportsApi';
import type { FamilyColumn, RevenueFamilyCell, RevenueMonthRow } from '@/api/reports/types';
import { familyColorOrNeutral } from '@/components/familyPalette';
import { OverlapPopover } from '@/components/OverlapPopover';
import { EM_DASH } from '@/components/rosterFormat';
import type { ResourceStatus } from '@/hooks/useResource';
import { useCopy } from '@/hooks/useCopy';
import { ErrorDetails } from '@/components/ErrorDetails';
import { familyViewKey } from '@/config/viewPrefs';
import { CompareCaption, type CompareMode } from '@/components/CompareSupport';

const NONE_HIDDEN: ReadonlySet<string> = new Set();

const cellSx = { whiteSpace: 'nowrap' as const };
const rowACellSx = { whiteSpace: 'nowrap' as const, borderBottom: 'none' };
const rowLabelSx = {
  whiteSpace: 'nowrap' as const,
  color: 'text.secondary',
  fontSize: 12,
};

export interface RevenueMonthsTableProps {
  rows: readonly RevenueMonthRow[];
  familyColumns: readonly FamilyColumn[];
  currency: string;
  dimension: string | null;
  transitional: boolean;
  hasMembershipColumn: boolean;
  status: ResourceStatus;
  /** The load error, shown with copyable details when `status` is `'error'`. */
  error?: unknown;
  totalElements: number;
  page: number;
  size: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (size: number) => void;
  /** Column keys to omit (`monthTotal`, `family:<slug>`, `overlap`). */
  hidden?: ReadonlySet<string>;
  compare?: CompareMode;
}

function familyCell(row: RevenueMonthRow, slug: string | null): RevenueFamilyCell | undefined {
  return row.families.find((cell) => cell.familySlug === slug);
}

function FamilyHeader({ column }: { column: FamilyColumn }) {
  const palette = familyColorOrNeutral(column.color);
  return (
    <>
      {palette ? (
        <Box
          component="span"
          aria-hidden
          data-testid={`family-swatch-${column.slug ?? 'unmapped'}`}
          sx={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: '2px',
            mr: 0.75,
            verticalAlign: 'middle',
            bgcolor: palette.bg,
            border: '1px solid',
            borderColor: palette.bg,
          }}
        />
      ) : null}
      {column.displayName}
    </>
  );
}

function MoneyOrLink({
  text,
  href,
  link,
}: {
  text: string;
  href: string;
  link: boolean;
}): ReactNode {
  if (!link) return text;
  return (
    <Link component={RouterLink} to={href}>
      {text}
    </Link>
  );
}

export function RevenueMonthsTable({
  rows,
  familyColumns,
  currency,
  dimension,
  transitional,
  hasMembershipColumn,
  status,
  error,
  totalElements,
  page,
  size,
  onPageChange,
  onRowsPerPageChange,
  hidden = NONE_HIDDEN,
  compare = 'off',
}: RevenueMonthsTableProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const na = t('reports.revenue.na', 'N/A');
  const title = t('page.revenueStats.title', 'Revenue Stats');
  const fmt = (amount: number) => formatMoney(amount, currency, i18n.language);
  const avgLabel = (value: number | null) => (value === null ? na : fmt(value));
  const reportParams = { dimension, currency };
  const [anchor, setAnchor] = useState<{ month: string; el: HTMLElement } | null>(null);
  const openRow = anchor !== null ? rows.find((row) => row.month === anchor.month) : undefined;
  const showMonthTotal = !hidden.has('monthTotal');
  const showOverlap = !hidden.has('overlap');
  const visibleFamilies = familyColumns.filter((column) => !hidden.has(familyViewKey(column.slug)));
  const emptyColSpan =
    2 + (showMonthTotal ? 1 : 0) + visibleFamilies.length + (showOverlap ? 1 : 0);

  return (
    <>
      {status === 'loading' ? (
        <Skeleton variant="rounded" height={240} role="status" />
      ) : null}

      {status === 'error' ? (
        <Alert severity="error">
          {t('reports.loadError', 'Couldn’t load this report.')}
          <ErrorDetails
            intent="read"
            error={error}
            headline={t('reports.loadError', 'Couldn’t load this report.')}
          />
        </Alert>
      ) : null}

      {status === 'ready' ? (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label={title}>
              <TableHead>
                <TableRow>
                  <TableCell sx={cellSx}>{t('reports.revenue.columns.month', 'Month')}</TableCell>
                  <TableCell sx={cellSx} />
                  {showMonthTotal ? (
                    <TableCell sx={cellSx} align="right">
                      {t('reports.revenue.columns.monthTotal', 'Month total')}
                    </TableCell>
                  ) : null}
                  {visibleFamilies.map((column) => (
                    <TableCell
                      key={column.slug ?? 'unmapped'}
                      sx={cellSx}
                      align="right"
                    >
                      <FamilyHeader column={column} />
                    </TableCell>
                  ))}
                  {showOverlap ? (
                    <TableCell sx={cellSx} align="right">
                      {t('reports.revenue.columns.overlap', 'Overlap')}
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={emptyColSpan} sx={cellSx}>
                      <Typography variant="body2" color="text.secondary">
                        {t('reports.revenue.empty', 'No months to show.')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const membershipHref = appendReportParams(
                      `/reports/revenue/${row.month}/membership`,
                      reportParams,
                    );
                    const armLink = hasMembershipColumn && !transitional;
                    const monthLabel = formatMonthLabel(row.month, i18n.language);
                    const overlapTitle =
                      row.overlapLineCount > 0
                        ? t(
                            'reports.revenue.overlapLines',
                            '{{n}} lines counted in more than one family',
                            { n: row.overlapLineCount },
                          )
                        : undefined;

                    return (
                      <Fragment key={row.month}>
                        <TableRow>
                          <TableCell sx={rowACellSx} rowSpan={2}>
                            {armLink ? (
                              <Link component={RouterLink} to={membershipHref}>
                                {monthLabel}
                              </Link>
                            ) : (
                              monthLabel
                            )}
                          </TableCell>
                          <TableCell sx={{ ...rowACellSx, ...rowLabelSx }}>
                            {t('reports.revenue.rowAvg', 'Avg per member')}
                          </TableCell>
                          {showMonthTotal ? (
                            <TableCell sx={rowACellSx} align="right">
                              <MoneyOrLink
                                text={avgLabel(row.arm)}
                                href={membershipHref}
                                link={armLink}
                              />
                              <CompareCaption
                                comparisons={row.comparisons}
                                mode={compare}
                                getMetric={(v) => v.arm}
                                formatValue={fmt}
                                testId="arm"
                              />
                            </TableCell>
                          ) : null}
                          {visibleFamilies.map((column) => {
                            const cell = familyCell(row, column.slug);
                            const href = appendReportParams(
                              `/reports/revenue/${row.month}/${familySlugToPath(column.slug)}`,
                              reportParams,
                            );
                            return (
                              <TableCell
                                key={`${row.month}-avg-${column.slug ?? 'unmapped'}`}
                                sx={rowACellSx}
                                align="right"
                              >
                                <MoneyOrLink
                                  text={avgLabel(cell?.average ?? null)}
                                  href={href}
                                  link={!transitional}
                                />
                                <CompareCaption
                                  comparisons={row.comparisons}
                                  mode={compare}
                                  getMetric={(v) =>
                                    v.families.find((f) => f.familySlug === column.slug)?.average ??
                                    null
                                  }
                                  formatValue={fmt}
                                  testId={`family-average-${column.slug}`}
                                />
                              </TableCell>
                            );
                          })}
                          {showOverlap ? (
                            <TableCell
                              sx={rowACellSx}
                              align="right"
                              rowSpan={2}
                              title={overlapTitle}
                            >
                              {row.overlapLineCount > 0 ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  aria-label={t(
                                    'reports.revenue.overlapOpen',
                                    'Show overlapping products for {{month}}',
                                    { month: monthLabel },
                                  )}
                                  aria-haspopup="dialog"
                                  onClick={(event) => {
                                    setAnchor({ month: row.month, el: event.currentTarget });
                                  }}
                                >
                                  {row.overlapLineCount}
                                </Button>
                              ) : (
                                EM_DASH
                              )}
                            </TableCell>
                          ) : null}
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ ...cellSx, ...rowLabelSx }}>
                            {t('reports.revenue.rowTotal', 'Total revenue')}
                          </TableCell>
                          {showMonthTotal ? (
                            <TableCell sx={cellSx} align="right">
                              {fmt(row.totalRevenue)}
                              <CompareCaption
                                comparisons={row.comparisons}
                                mode={compare}
                                getMetric={(v) => v.totalRevenue}
                                formatValue={fmt}
                                testId="totalRevenue"
                              />
                            </TableCell>
                          ) : null}
                          {visibleFamilies.map((column) => {
                            const cell = familyCell(row, column.slug);
                            const href = appendReportParams(
                              `/reports/revenue/${row.month}/${familySlugToPath(column.slug)}`,
                              reportParams,
                            );
                            return (
                              <TableCell
                                key={`${row.month}-total-${column.slug ?? 'unmapped'}`}
                                sx={cellSx}
                                align="right"
                              >
                                <MoneyOrLink
                                  text={fmt(cell?.revenue ?? 0)}
                                  href={href}
                                  link={!transitional}
                                />
                                <CompareCaption
                                  comparisons={row.comparisons}
                                  mode={compare}
                                  getMetric={(v) =>
                                    v.families.find((f) => f.familySlug === column.slug)?.revenue ??
                                    null
                                  }
                                  formatValue={fmt}
                                  testId={`family-revenue-${column.slug}`}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={(_event, next) => onPageChange(next)}
            rowsPerPage={size}
            onRowsPerPageChange={(event) => {
              onRowsPerPageChange(Number(event.target.value));
            }}
            rowsPerPageOptions={[24, 50, 100]}
          />
          <OverlapPopover
            open={anchor !== null && openRow !== undefined}
            anchorEl={anchor?.el ?? null}
            onClose={() => setAnchor(null)}
            month={openRow?.month ?? ''}
            overlapLineCount={openRow?.overlapLineCount ?? 0}
            products={openRow?.overlapProducts ?? []}
            truncated={openRow?.overlapProductsTruncated ?? 0}
            familyColumns={familyColumns}
            dimension={dimension}
          />
        </>
      ) : null}
    </>
  );
}
